import React, { useState, useCallback } from 'react';
import classnames from 'classnames/bind';

import { Upload, Download, CheckCircle } from 'lucide-react';

import BaseModal from '@MODALS/BaseModal';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import FileUploadDropzone from '@COMPONENTS/COMMON/inputs/file/FileUploadDropzone';
import BasicSelect from '@COMPONENTS/COMMON/inputs/select/inputs/BasicSelect';
import { notify } from '@NOTIFICATION/Notificator';
import { useDownloadTemplate, useImportVendors, useGetAllWorkspaces } from '@SERVICES';

import styles from './ImportVendorsModal.module.scss';

const cx: CX = classnames.bind(styles);

interface ImportVendorsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void; // Callback to refresh vendors list
}

interface ImportError {
    row: number;
    field: string;
    message: string;
}

interface ImportWarning {
    row: number;
    field: string;
    message: string;
}

interface ImportResponse {
    success: boolean;
    imported: number;
    errors: ImportError[];
    warnings: ImportWarning[];
    message: string;
}

function ImportVendorsModal(props: ImportVendorsModalProps) {
    const { isOpen, onClose, onSuccess } = props;

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [importResult, setImportResult] = useState<ImportResponse | null>(null);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | undefined>(undefined);

    // API hooks
    const [downloadTemplate, { isLoading: isDownloading }] = useDownloadTemplate();
    const [importVendors, { isLoading: isUploading }] = useImportVendors();
    const { data: workspacesData } = useGetAllWorkspaces();

    // Prepare workspace options for dropdown
    const workspaceOptions = React.useMemo(() => {
        if (!workspacesData) return [];
        
        return workspacesData.map((workspace: any) => ({
            value: workspace.id,
            label: workspace.name,
        }));
    }, [workspacesData]);

    // Handle modal close
    const handleClose = useCallback(() => {
        if (isUploading) return; // Prevent closing during upload
        
        setSelectedFile(null);
        setUploadComplete(false);
        setImportResult(null);
        setSelectedWorkspaceId(undefined);
        onClose();
    }, [isUploading, onClose]);

    // Handle file selection
    const handleFileSelect = useCallback((files: File[]) => {
        if (files.length > 0) {
            setSelectedFile(files[0]);
            setUploadComplete(false);
            setImportResult(null);
        }
    }, []);

    // Clear selected file
    const handleFileClear = useCallback(() => {
        setSelectedFile(null);
        setUploadComplete(false);
        setImportResult(null);
    }, []);

    // Handle workspace selection
    const handleWorkspaceChange = useCallback((value: number | null) => {
        setSelectedWorkspaceId(value || undefined);
    }, []);

    // Download template file
    const handleDownloadTemplate = useCallback(async () => {
        try {
            const blob = await downloadTemplate({
                include_sample_data: true,
            }).unwrap();
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'vendor_import_template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            notify.success('Template downloaded successfully!');
        } catch (error: any) {
            console.error('Template download error:', error);
            
            // Handle different error types
            let errorMessage = 'Failed to download template';
            
            if (error?.message) {
                errorMessage = error.message;
            } else if (error?.status) {
                errorMessage = `Download failed: HTTP ${error.status}`;
                if (error.status === 404) {
                    errorMessage = 'Template endpoint not found. Please check if the API endpoint is available.';
                }
            }
            
            notify.error(errorMessage);
        }
    }, [downloadTemplate]);

    // Submit import
    const handleImport = useCallback(async () => {
        if (!selectedFile) {
            notify.error('Please select a file to import');
            return;
        }
        
        try {
            const result = await importVendors({ 
                file: selectedFile,
                workspace_id: selectedWorkspaceId,
            }).unwrap();
            setImportResult(result);
            setUploadComplete(true);

            // Show success/warning messages
            if (result.success) {
                if (result.imported > 0) {
                    notify.success(
                        `Successfully imported ${result.imported} vendor${result.imported !== 1 ? 's' : ''}!`
                    );
                }
                
                // Show warnings if any
                if (result.warnings && result.warnings.length > 0) {
                    result.warnings.forEach((warning) => {
                        notify.warning(`Row ${warning.row}, ${warning.field}: ${warning.message}`);
                    });
                }
                
                // Call success callback to refresh vendors list
                if (onSuccess) {
                    onSuccess();
                }
            }

            // Show errors if any
            if (result.errors && result.errors.length > 0) {
                result.errors.forEach((error) => {
                    notify.error(`Row ${error.row}, ${error.field}: ${error.message}`);
                });
            }

            // Auto-close modal after success (with delay to show results)
            if (result.success && result.imported > 0) {
                setTimeout(() => {
                    handleClose();
                }, 3000);
            }
        } catch (error: any) {
            console.error('Import error:', error);
            
            // Handle different error types
            let errorMessage = 'Failed to import vendors';
            
            if (error?.message) {
                errorMessage = error.message;
            } else if (error?.status) {
                errorMessage = `Import failed: HTTP ${error.status}`;
                if (error.status === 404) {
                    errorMessage = 'Import endpoint not found. Please check if the API endpoint is available.';
                } else if (error.status === 400) {
                    errorMessage = 'Invalid file format or content. Please check your file and try again.';
                }
            }
            
            notify.error(errorMessage);
        }
    }, [selectedFile, selectedWorkspaceId, onSuccess, handleClose, importVendors]);

    if (!isOpen) return null;

    return (
        <BaseModal
            onClose={handleClose}
            style={{
                width: '90%',
                maxWidth: '720px',
            }}
        >
            <div className={cx('import-vendors-modal')}>
                {/* Modal Header */}
                <div className={cx('modal-header')}>
                    <div className={cx('icon-wrapper')}>
                        {uploadComplete ? (
                            <CheckCircle 
                                className={cx('modal-icon', 'success-icon')} 
                                size={24}
                                strokeWidth={2}
                            />
                        ) : (
                            <Upload 
                                className={cx('modal-icon')} 
                                size={24}
                                strokeWidth={2}
                            />
                        )}
                    </div>
                    <div className={cx('header-content')}>
                        <h2 className={cx('title')}>
                            {uploadComplete ? 'Import Complete' : 'Import Vendors'}
                        </h2>
                        <p className={cx('subtitle')}>
                            {(() => {
                                if (!uploadComplete) {
                                    return 'Upload an Excel file to import multiple vendors at once';
                                }
                                if (importResult?.imported) {
                                    return `Successfully imported ${importResult.imported} vendor${importResult.imported !== 1 ? 's' : ''}`;
                                }
                                return 'Import completed with issues';
                            })()}
                        </p>
                    </div>
                </div>

                {/* Modal Content */}
                <div className={cx('modal-content')}>
                    {!uploadComplete ? (
                        <>
                            {/* Template Download Section */}
                            <div className={cx('template-section')}>
                                <div className={cx('section-header')}>
                                    <div className={cx('section-info')}>
                                        <h3 className={cx('section-title')}>Step 1: Download Template</h3>
                                        <p className={cx('section-description')}>
                                            Start by downloading our Excel template with the required columns, format guidelines, and sample data
                                        </p>
                                    </div>
                                </div>
                                
                                <BasicButton
                                    variant="neutral"
                                    size="medium"
                                    title={isDownloading ? 'Downloading...' : 'Download Template'}
                                    icon="download"
                                    onClick={handleDownloadTemplate}
                                    isProcessing={isDownloading}
                                    style={{ width: 'auto' }}
                                />
                            </div>

                            {/* Workspace Selection Section */}
                            <div className={cx('workspace-section')}>
                                <div className={cx('section-header')}>
                                    <div className={cx('section-info')}>
                                        <h3 className={cx('section-title')}>Step 2: Select Workspace (Optional)</h3>
                                        <p className={cx('section-description')}>
                                            Choose a workspace to assign imported vendors to. If not selected, vendors will be assigned to your default workspace.
                                        </p>
                                    </div>
                                </div>
                                
                                <BasicSelect
                                    title="Workspace"
                                    placeholder="Select workspace (optional)"
                                    isSearchable
                                    options={workspaceOptions}
                                    value={selectedWorkspaceId}
                                    onChange={handleWorkspaceChange}
                                    isClearable
                                    menuPlacement="bottom"
                                />
                            </div>

                            {/* File Upload Section */}
                            <div className={cx('upload-section')}>
                                <div className={cx('section-header')}>
                                    <div className={cx('section-info')}>
                                        <h3 className={cx('section-title')}>Step 3: Upload Completed File</h3>
                                        <p className={cx('section-description')}>
                                            Upload your completed Excel file with vendor data
                                        </p>
                                    </div>
                                </div>

                                <FileUploadDropzone
                                    title="Click or drag Excel file to upload"
                                    onDrop={handleFileSelect}
                                    onClear={handleFileClear}
                                    selectedFile={selectedFile}
                                    accept={{
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                        'application/vnd.ms-excel': ['.xls'],
                                    }}
                                    maxSize={10 * 1024 * 1024} // 10MB
                                    helpText="Excel files (.xlsx, .xls) only, max 10MB"
                                />
                            </div>

                            {/* Instructions */}
                            <div className={cx('instructions-section')}>
                                <h4 className={cx('instructions-title')}>Important Notes:</h4>
                                <ul className={cx('instructions-list')}>
                                    <li>Use the provided template to ensure correct formatting</li>
                                    <li>Required fields: name, company</li>
                                    <li>Optional fields: address, email, phone, contact_person, notes, status, contract_status, website, is_visible</li>
                                    <li>Duplicate vendors (by name in same workspace) will be skipped</li>
                                    <li>Maximum 1000 vendors per import</li>
                                    <li>Status values: &apos;active&apos; or &apos;inactive&apos; (defaults to &apos;active&apos;)</li>
                                    <li>is_visible values: TRUE or FALSE (defaults to TRUE)</li>
                                </ul>
                            </div>
                        </>
                    ) : (
                        <div className={cx('success-content')}>
                            <div className={cx('success-icon-large')}>
                                <CheckCircle size={64} strokeWidth={1.5} />
                            </div>
                            <div className={cx('success-message')}>
                                {importResult?.imported ? (
                                    <>
                                        <h3>Import Successful!</h3>
                                        <p>
                                            {importResult.imported} vendor{importResult.imported !== 1 ? 's have' : ' has'} been successfully imported and {importResult.imported !== 1 ? 'are' : 'is'} now available in the system.
                                        </p>
                                        {importResult.errors && importResult.errors.length > 0 && (
                                            <p className={cx('error-summary')}>
                                                {importResult.errors.length} row{importResult.errors.length !== 1 ? 's' : ''} had errors and were skipped.
                                            </p>
                                        )}
                                        {importResult.warnings && importResult.warnings.length > 0 && (
                                            <p className={cx('warning-summary')}>
                                                {importResult.warnings.length} warning{importResult.warnings.length !== 1 ? 's' : ''} were noted during import.
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <h3>Import Completed</h3>
                                        <p>The import process completed, but no new vendors were added. Please check the error messages and try again.</p>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal Actions */}
                <div className={cx('modal-actions')}>
                    {!uploadComplete ? (
                        <>
                            <BasicButton
                                variant="neutral"
                                size="medium"
                                title="Cancel"
                                onClick={handleClose}
                                locked={isUploading}
                                style={{ flex: 1 }}
                            />
                            <BasicButton
                                variant="success"
                                size="medium"
                                title={isUploading ? 'Importing...' : 'Import Vendors'}
                                onClick={handleImport}
                                isProcessing={isUploading}
                                locked={!selectedFile}
                                style={{ flex: 2 }}
                            />
                        </>
                    ) : (
                        <BasicButton
                            variant="primary"
                            size="medium"
                            title="Close"
                            onClick={handleClose}
                            style={{ width: '100%' }}
                        />
                    )}
                </div>
            </div>
        </BaseModal>
    );
}

ImportVendorsModal.defaultProps = {
    onSuccess: undefined,
};

export default ImportVendorsModal; 